/**
 * StatusBar — Bottom strip with cursor coordinates, scale, CRS, selection count
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Public, FiberManualRecord, TouchApp } from '@mui/icons-material';
import { tokens } from '../../theme';
import { useGeoveraTheme } from '../../theme/ThemeContext';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { selectSelectedCount } from '../../store/slices/selectionSlice';
import { openCRSInfoDialog } from '../../store/slices/coordinateSystemSlice';
import { getCRSDisplayName } from '../../services/CoordinateSystemManager';

const StatusBar: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const selectedCount = useAppSelector(selectSelectedCount);
  const mapCRS = useAppSelector((s) => s.coordinateSystem.mapCRS);
  const layers = useAppSelector((s) => s.layers.layers);
  const visibleLayers = layers.filter((l) => l.visible).length;
  const totalFeatures = layers.reduce((sum, l) => sum + (l.features?.length || 0), 0);

  const [cursorCoords, setCursorCoords] = useState('—');
  const [digipin, setDigipin] = useState<string | null>(null);
  const [mapScale, setMapScale] = useState('1:1 000 000');

  useEffect(() => {
    const coordHandler = (e: CustomEvent) => {
      const detail = e.detail;
      if (detail && typeof detail === 'object') {
        setCursorCoords(detail.formatted || '—');
        setDigipin(detail.digipin || null);
      } else if (typeof detail === 'string') {
        setCursorCoords(detail);
        setDigipin(null);
      }
    };
    const scaleHandler = (e: CustomEvent) => {
      setMapScale(e.detail);
    };
    window.addEventListener('map:cursorcoords', coordHandler as EventListener);
    window.addEventListener('map:scale', scaleHandler as EventListener);
    return () => {
      window.removeEventListener('map:cursorcoords', coordHandler as EventListener);
      window.removeEventListener('map:scale', scaleHandler as EventListener);
    };
  }, []);

  const StatusItem: React.FC<{
    children: React.ReactNode;
    onClick?: () => void;
    tooltip?: string;
    highlight?: boolean;
    sx?: any;
  }> = ({ children, onClick, tooltip, highlight, sx }) => {
    const content = (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          height: tokens.layout.statusBarHeight,
          px: 1,
          cursor: onClick ? 'pointer' : 'default',
          color: highlight ? palette.accent.primary : palette.text.muted,
          transition: `background-color ${tokens.animation.fast}, color ${tokens.animation.fast}`,
          '&:hover': onClick
            ? {
                backgroundColor: palette.accent.primaryDim,
                color: palette.accent.primary,
              }
            : {},
          ...sx,
        }}
      >
        {children}
      </Box>
    );
    return tooltip ? (
      <Tooltip title={tooltip} placement="top" arrow>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  return (
    <Box
      sx={{
        height: tokens.layout.statusBarHeight,
        minHeight: tokens.layout.statusBarHeight,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: palette.bg.raised,
        borderTop: `1px solid ${palette.border.base}`,
        fontSize: tokens.fontSize.xs,
        zIndex: 10,
      }}
    >
      {/* ─── Left: Status indicator ──────────── */}
      <StatusItem tooltip="System status">
        <FiberManualRecord sx={{ fontSize: 8, color: palette.accent.success }} />
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: 'inherit' }}>Ready</Typography>
      </StatusItem>

      {/* ─── Layers info ─────────────────────── */}
      <StatusItem
        tooltip={`${totalFeatures} total features in ${visibleLayers} visible layers`}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      >
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: 'inherit' }}>
          {visibleLayers} {visibleLayers === 1 ? 'layer' : 'layers'} ·{' '}
          {totalFeatures.toLocaleString()} features
        </Typography>
      </StatusItem>

      <Box sx={{ flex: 1 }} />

      {/* ─── Right: Cursor, scale, CRS, selection ─────── */}
      {selectedCount > 0 && (
        <StatusItem highlight tooltip={`${selectedCount} feature(s) currently selected`}>
          <TouchApp sx={{ fontSize: 12 }} />
          <Typography sx={{ fontSize: tokens.fontSize.xs, color: 'inherit', fontWeight: 500 }}>
            {selectedCount} selected
          </Typography>
        </StatusItem>
      )}

      {digipin && (
        <StatusItem tooltip={`National DIGIPIN 4m Geo-Grid code: ${digipin}`}>
          <Box
            sx={{
              px: 0.8,
              py: 0.25,
              borderRadius: 0.5,
              backgroundColor: 'rgba(217, 119, 6, 0.16)',
              border: '1px solid rgba(217, 119, 6, 0.45)',
              color: '#F59E0B',
              fontWeight: 700,
              fontSize: '11px',
              fontFamily: 'monospace',
              letterSpacing: '0.04em',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            DIGIPIN: {digipin}
          </Box>
        </StatusItem>
      )}

      <StatusItem tooltip="Cursor coordinates" sx={{ display: { xs: 'none', sm: 'flex' } }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: 'inherit',
            fontFamily: 'monospace',
          }}
        >
          {cursorCoords}
        </Typography>
      </StatusItem>

      <StatusItem tooltip="Map scale" sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Typography
          sx={{ fontSize: tokens.fontSize.xs, color: 'inherit', fontFamily: 'monospace' }}
        >
          {mapScale}
        </Typography>
      </StatusItem>

      <StatusItem
        tooltip="Coordinate Reference System — click for details"
        onClick={() => dispatch(openCRSInfoDialog(mapCRS))}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      >
        <Public sx={{ fontSize: 12 }} />
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: 'inherit' }}>
          {getCRSDisplayName(mapCRS)}
        </Typography>
      </StatusItem>
    </Box>
  );
};

export default StatusBar;
